import {
    Delete,
    KeyboardArrowDown,
    LocalOfferOutlined
} from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { updateDashboardFunc } from '../../../lib/helper';
import { TagGroup } from '../../../types/common';
import { Dashboard } from '../../../types/files';

type Tag = {
    id: number;
    tag: string;
    selected: boolean;
};

const useStyles = makeStyles({
    dialogPaper: {
        borderRadius: 24,
        maxWidth: '800px',
        width: '90%',
        maxHeight: '85vh',
    },
    dialogTitle: {
        padding: '28px 32px',
        borderBottom: '1px solid #e5e7eb',
    },
    dialogContent: {
        padding: '32px !important',
        overflowY: 'auto',
        flex: 1,
    },
    dialogActions: {
        padding: '20px 32px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sectionLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#64748b',
        display: 'block',
    },
    hint: {
        fontSize: '13px',
        color: '#94a3b8',
        lineHeight: 1.5,
        marginTop: '-6px',
    },
    fieldNameInput: {
        '& .MuiOutlinedInput-root': {
            height: '45px',
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#1eb6a8',
            borderRadius: 12,
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
            '&.Mui-error fieldset': {
                borderColor: '#dc2626 !important',
            },
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#dc2626',
            fontFamily: 'Inter',
            fontSize: '0.8rem',
            marginLeft: 0,
        },
    },
    editButton: {
        width: '40px',
        height: '40px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        '&:hover': {
            borderColor: '#1eb6a8',
            backgroundColor: '#f0fdf9',
        },
    },
    textarea: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontFamily: 'Lato',
            fontSize: '14px',
            color: '#1e293b',
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
        },
    },
    addTagInput: {
        '& .MuiOutlinedInput-root': {
            height: '45px',
            borderRadius: 12,
            fontFamily: 'Lato',
            fontSize: '14px',
            color: '#1e293b',
            background: '#fff',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8 !important',
            },
            '&.Mui-focused': {
                background: '#f0fdf9 !important',
            },
        },
    },
    addButton: {
        height: '45px',
        padding: '12px 24px',
        borderRadius: '10px',
        border: '2px solid #1eb6a8',
        backgroundColor: 'white',
        color: '#1eb6a8',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#1eb6a8',
            color: 'white',
            border: '2px solid #1eb6a8',
        },
    },
    bulkControlsPaper: {
        padding: '8px 12px',
        backgroundColor: '#f8f9fc',
        borderRadius: '8px',
        boxShadow: 'none',
    },
    bulkDeleteButton: {
        padding: '6px 12px',
        backgroundColor: 'white',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        color: '#dc2626',
        fontSize: '13px',
        fontWeight: 500,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#fee',
            borderColor: '#dc2626',
        },
        '&:disabled': {
            backgroundColor: '#f5f5f5',
            borderColor: '#e5e7eb',
            color: '#cbd5e1',
        },
    },
    tagsListBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        minHeight: '45px',
        padding: '12px',
        backgroundColor: 'white',
        borderRadius: '10px',
        border: '2px solid #e5e7eb',
        position: 'relative',
    },
    emptyTags: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: '#94a3b8',
        fontSize: '14px',
        fontStyle: 'italic',
    },
    tagChip: {
        padding: '2px 8px',
        borderRadius: 20,
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#64748b',
        backgroundColor: '#f1f5f9',
        height: 'auto',
        '& .MuiChip-deleteIcon': {
            color: '#94a3b8',
            fontSize: '20px',
            marginLeft: '10px',
            '&:hover': {
                color: '#64748b',
            },
        },
    },
    closeIcon: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#94a3b8',
        padding: 'unset',
    },
    copyPasteButton: {
        color: '#1eb6a8',
        fontSize: '13px',
        fontWeight: 500,
        textTransform: 'none',
        padding: '4px 8px',
        borderRadius: '6px',
        minWidth: 'auto',
        '&:hover': {
            backgroundColor: '#f0fdf9',
        },
    },
    importButton: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#1eb6a8',
        color: 'white',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#1a9d91',
        },
    },
    formContainer: {
        minWidth: '80px',
        width: 'fit-content',
        "& > .MuiInputBase-root":{
            height: '45px',
            width: 'auto',
            borderRadius: 12,
            fontWeight: 500,
            color: '#1eb6a8',
            fontSize: '14px',
            '& fieldset': {
                borderWidth: '2px !important',
                borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
                borderColor: '#1eb6a8',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1eb6a8',
            },
        },
        '& .MuiSelect-icon': {
            color: '#1eb6a8',
        },
    },
    deleteFieldButton: {
        height: '40px',
        color: '#dc2626',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '8px',
        padding: '10px 16px',
        '&:hover': {
            backgroundColor: '#fee',
        },
    },
    backButton: {
        height: '40px',
        padding: '10px 24px',
        border: '2px solid #e5e7eb',
        borderRadius: '10px',
        backgroundColor: 'white',
        color: '#64748b',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            borderColor: '#cbd5e1',
            backgroundColor: 'white',
        },
    },
    saveButton: {
        height: '40px',
        padding: '10px 24px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&.Mui-disabled': {
            backgroundColor: '#e5e7eb',
            color: '#94a3b8',
        },
    },
    saveButtonActive: {
        backgroundColor: '#1eb6a8',
        color: 'white',
        '&:hover': {
            backgroundColor: '#1a9d91',
        },
    },
    checkAll: {
        color: '#64748b',
        padding: 0,
        '&.Mui-checked': { color: '#1eb6a8' },
        '&.MuiCheckbox-indeterminate': { color: '#1eb6a8' }
    },
    selectAll: {
        fontFamily: 'Inter',
        fontSize: '13px',
        color: '#64748b',
        fontWeight: 500,
        cursor: 'pointer',
        userSelect: 'none'
    },
    checkOne: {
        color: '#64748b',
        padding: 0,
        '&.Mui-checked': { color: '#1eb6a8' }
    },
    menu :{
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: '#1eb6a8',
    }
});

const ManageCustomTagsModal: FC<{
    open: boolean;
    filterType?: string;
    initialName?: string;
    onClose: () => void;
  }> = ({ open, filterType, initialName, onClose }) => {
    const classes = useStyles();
    const { settingsLoaded, tagGroups, setTagGroups, customFields } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);

    const [title, setTitle] = useState<string>('');
    const [tags, setTags] = useState<Tag[]>([]);
    const [tagsPerDashboard, setTagsPerDashboard] = useState<number>(3);
    const [additionalContextQuery, SetAdditionalContextQuery] = useState<string>('');

    const [newTagInput, setNewTagInput] = useState<string>('');
    const [showCopyPaste, setShowCopyPaste] = useState<boolean>(false);
    const [copyPasteInput, setCopyPasteInput] = useState<string>('');
    const [selectAll, setSelectAll] = useState<boolean>(false);

    const selectedCount = useMemo(() => tags.filter(t => t.selected).length, [tags]);
    const hasSelectedTags = useMemo(() => selectedCount > 0, [selectedCount]);
    const isIndeterminate = useMemo(() => hasSelectedTags && selectedCount < tags.length, [hasSelectedTags, selectedCount, tags.length]);

    const isDuplicateName = useMemo(() => {
        const trimmed = title.trim().toLowerCase();
        if (!trimmed) return false;
        if (filterType && trimmed === filterType.trim().toLowerCase()) return false;
        return tagGroups.some(({ title: t }) => t.trim().toLowerCase() === trimmed)
            || customFields.some(({ name }) => name.trim().toLowerCase() === trimmed);
    }, [title, filterType, tagGroups, customFields]);

    const isSaveDisabled = useMemo(() => !title.trim() || isDuplicateName, [title, isDuplicateName]);

    const handleAddTag = useCallback(() => {
        const trimmedTag = newTagInput.trim();
        if (trimmedTag && !tags.some(t => t.tag === trimmedTag)) {
            setTags(prev => [...prev, { id: Date.now(), tag: trimmedTag, selected: false }]);
            setNewTagInput('');
        }
    }, [newTagInput, tags]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    }, [handleAddTag]);

    const handleRemoveTag = useCallback((id: number) => {
        setTags(prev => prev.filter(tag => tag.id !== id));
    }, []);

    const handleToggleTag = useCallback((id: number) => {
        setTags(prev => prev.map(tag =>
            tag.id === id ? { ...tag, selected: !tag.selected } : tag
        ));
    }, []);

    const handleToggleCopyPaste = useCallback(() => {
        setShowCopyPaste(prev => !prev);
    }, []);

    const handleSelectAll = useCallback(() => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        setTags(prev => prev.map(tag => ({ ...tag, selected: newSelectAll })));
    }, [selectAll]);

    const handleDeleteSelected = useCallback(() => {
        setTags(prev => prev.filter(tag => !tag.selected));
        setSelectAll(false);
    }, []);

    const handleImportTags = useCallback(() => {
        const newTags = copyPasteInput
            .split(',')
            .map(t => t.trim())
            .filter(t => t && !tags.some(tag => tag.tag === t))
            .map(text => ({ id: Date.now() + Math.random(), tag: text, selected: false }));

        setTags(prev => [...prev, ...newTags]);
        setCopyPasteInput('');
        setShowCopyPaste(false);
    }, [copyPasteInput, tags]);

    const handleUpdateTagGroups = useCallback((title: string, tags?: string[], tagsCount?: number, context?: string, create?: boolean) => {
        if (!create) {
            const lookUpTagGroup = tagGroups.find(group => group.title.toLowerCase() === title.toLowerCase());

            if (!lookUpTagGroup) {
                setTagGroups(prev => [...prev, ({ title, tags, tagsPerDashboard: tagsCount || 3, additionalContextQuery: context || '' } as TagGroup)]
                    .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));
            }  else {
                setTagGroups(prev => prev.map(tagGroup => tagGroup.title.toLowerCase() === title.toLowerCase()
                    ? ({ ...tagGroup, tags: tags ?? [], tagsPerDashboard: tagsCount || 3, additionalContextQuery: context || '' }) : tagGroup)
                        .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));
            }
        }
        setTitle(title);
        setTags(tags?.map((tag, i) => ({id: i, tag, selected: false})) ?? []);
        setTagsPerDashboard(tagsCount || 3);
        SetAdditionalContextQuery(context || '');
    // eslint-disable-next-line
    }, [tagGroups]);

    // eslint-disable-next-line
    const handleEditTitle = useCallback((oldTitle: string, newTitle: string, create?: boolean) => {
        if (!create) {
            setTagGroups(prev => prev.map(tagGroup => tagGroup.title.toLowerCase() === oldTitle.toLowerCase()
                ? ({ ...tagGroup, title: newTitle.trim()  }) : tagGroup)
                    .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));
            setTitle(newTitle.trim());

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedTagGroup = false;

                const updatedDashboard = { ...dashboard,
                    tagGroups: dashboard.tagGroups?.map(group => {
                        if (group.title.toLowerCase() === oldTitle.toLowerCase()) {
                            hasRenamedTagGroup = true;
                            return ({ ...group, title: newTitle.trim() });
                        }

                        return group;
                    }) ?? [] };

                if (hasRenamedTagGroup)
                    updatedDashboards.push(updatedDashboard);
            });

            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
           handleUpdateTagGroups(newTitle.trim(), [], 3);
        }
    }, [setTagGroups, dashboards, setDashboards, handleUpdateTagGroups]);

    const handleUpdateContext = useCallback(() => {
        if (filterType && title.trim().toLowerCase() !== filterType.toLowerCase()) {
            // Title changed — rename tag group and update values in one operation
            setTagGroups(prev => prev.map(tagGroup => tagGroup.title.toLowerCase() === filterType.toLowerCase()
                ? ({ title: title.trim(), tags: tags.map(({tag}) => tag), tagsPerDashboard, additionalContextQuery: additionalContextQuery || '' }) : tagGroup)
                    .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedTagGroup = false;
                const updatedDashboard = { ...dashboard,
                    tagGroups: dashboard.tagGroups?.map(group => {
                        if (group.title.toLowerCase() === filterType.toLowerCase()) {
                            hasRenamedTagGroup = true;
                            return ({ ...group, title: title.trim() });
                        }
                        return group;
                    }) ?? [] };
                if (hasRenamedTagGroup)
                    updatedDashboards.push(updatedDashboard);
            });
            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
            handleUpdateTagGroups(title, tags.map(({tag}) => tag), tagsPerDashboard, additionalContextQuery || '');
        }
        onClose();
    }, [title, tags, tagsPerDashboard, additionalContextQuery, filterType, setTagGroups, dashboards, setDashboards, handleUpdateTagGroups, onClose]);

    const handleDeleteTagGroup = useCallback((title: string) => {
        setTagGroups(prev => prev.filter(tagGroup => tagGroup.title.toLowerCase() !== title.toLowerCase())
            .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({ title, tags, tagsPerDashboard, additionalContextQuery })));
        onClose();
    }, [onClose, setTagGroups]);

    useEffect(() => {
        if (settingsLoaded) {
            if (!!filterType) {
                if (!!tagGroups.length) {
                    const lookUpTagGroup = tagGroups.find(group => group?.title === filterType);

                    if (lookUpTagGroup) {
                        setTitle(lookUpTagGroup.title);
                        setTags(lookUpTagGroup.tags.map((tag, i) => ({ id: i, tag, selected: false })));
                        setTagsPerDashboard(lookUpTagGroup.tagsPerDashboard || 3);
                        SetAdditionalContextQuery(lookUpTagGroup.additionalContextQuery || '');
                    }
                }
            } else {
                handleUpdateTagGroups(initialName || '', [], 3, '', true);
                if (initialName) setTitle(initialName);
            }
        }
    // eslint-disable-next-line
    }, [settingsLoaded]);

    return (
        <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth={false}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <LocalOfferOutlined sx={{ color: '#64748b', fontSize: 24 }} />
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                        {'Custom tags'}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Stack direction="column" spacing={3.5} alignItems="stretch" justifyContent="flex-start">
                    <Stack direction="column" spacing={1.25} alignItems="stretch" justifyContent="flex-start">
                        <Typography className={classes.sectionLabel}>{'Field name'}</Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-start">
                            <TextField className={classes.fieldNameInput}
                                defaultValue="New Tags"
                                onChange={(e) => setTitle(e.target.value)}
                                error={isDuplicateName}
                                helperText={isDuplicateName ? 'A field or tag group with this name already exists' : ''}
                                value={title} fullWidth />
                        </Stack>
                    </Stack>
                    <Stack direction="column" spacing={1.25} alignItems="stretch" justifyContent="flex-start">
                        <Typography className={classes.sectionLabel}>{'Field description'}</Typography>
                        <Typography className={classes.hint}>
                            {'Enhance tag accuracy by providing additional context on how these should get assigned.'}
                        </Typography>
                        <TextField className={classes.textarea}
                            placeholder="Enter additional context here..."
                            onChange={(e) => SetAdditionalContextQuery(e.target.value)}
                            value={additionalContextQuery}
                            multiline fullWidth rows={4} />
                    </Stack>
                    <Stack direction="column" spacing={1.25} alignItems="stretch" justifyContent="flex-start">
                        <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between">
                            <Typography className={classes.sectionLabel}>{'Add tags:'}</Typography>
                            <Button variant="text"
                                className={classes.copyPasteButton}
                                onClick={handleToggleCopyPaste} disableRipple>
                                {'Copy/paste from list'}
                            </Button>
                        </Stack>
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-start">
                            <TextField className={classes.addTagInput}
                                placeholder="Add a new tag..."
                                onChange={(e) => setNewTagInput(e.target.value)}
                                value={newTagInput}
                                onKeyPress={handleKeyPress}
                                fullWidth />
                            <Button variant="outlined"
                                className={classes.addButton}
                                onClick={handleAddTag} disableRipple>
                                {'Add'}
                            </Button>
                        </Stack>
                        {tags.length > 0 && (
                            <Paper className={classes.bulkControlsPaper}>
                                <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                                        <Checkbox className={classes.checkAll}
                                            checked={selectAll}
                                            indeterminate={isIndeterminate}
                                            onChange={handleSelectAll}
                                            size="small" disableRipple />
                                        <Typography className={classes.selectAll}
                                            onClick={handleSelectAll}>
                                            {'Select all'}
                                        </Typography>
                                    </Stack>
                                    <Button className={classes.bulkDeleteButton}
                                        onClick={handleDeleteSelected}
                                        disabled={!hasSelectedTags}
                                        startIcon={<Delete sx={{ fontSize: 14 }} />}>
                                        {'Delete selected'}
                                    </Button>
                                </Stack>
                            </Paper>
                        )}
                        <Box className={classes.tagsListBox}>
                            {tags.length === 0 && (
                                <Typography className={classes.emptyTags}>
                                    {'No tags yet'}
                                </Typography>
                            )}
                            {tags.map(tag => (
                                <Stack key={tag.id} direction="row" spacing={1.25} alignItems="center" justifyContent="flex-start">
                                    <Chip className={classes.tagChip}
                                        label={
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                                                <Checkbox className={classes.checkOne}
                                                    checked={tag.selected}
                                                    onChange={() => handleToggleTag(tag.id)}
                                                    size="small" disableRipple />
                                                <Typography className={classes.selectAll}
                                                    onClick={handleSelectAll}>
                                                    {tag.tag}
                                                </Typography>
                                            </Stack>
                                        }
                                        onDelete={() => handleRemoveTag(tag.id)}
                                        deleteIcon={
                                            <Typography className={classes.closeIcon}> {'×'} </Typography>
                                        } />
                                </Stack>
                            ))}
                        </Box>
                        {showCopyPaste && (
                            <Stack direction="column" spacing={1.5} alignItems="stretch" justifyContent="flex-start">
                                <TextField className={classes.textarea}
                                    placeholder="Paste comma-separated tags here..."
                                    onChange={(e) => setCopyPasteInput(e.target.value)}
                                    value={copyPasteInput}
                                    multiline rows={3} fullWidth />
                                <Button variant="contained"
                                    className={classes.importButton}
                                    onClick={handleImportTags}
                                    disableRipple>
                                    {'Import Tags'}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="column" spacing={1.25} alignItems="stretch" justifyContent="flex-start">
                        <Typography className={classes.sectionLabel}>{'Number of tags per company:'}</Typography>
                        <FormControl className={classes.formContainer} variant="outlined">
                            <Select value={tagsPerDashboard}
                                onChange={(e) => setTagsPerDashboard(e.target.value as number)}
                                IconComponent={KeyboardArrowDown}>
                                {[1, 2, 3, 4, 5].map(num => (
                                    <MenuItem className={classes.menu} key={num} value={num}>{num}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.deleteFieldButton}
                    onClick={() => handleDeleteTagGroup(title)}
                    startIcon={<Delete sx={{ fontSize: 16 }} />}
                    disabled={isSaveDisabled} disableRipple>
                    {'Delete tags'}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                    <Button variant="outlined"
                        className={classes.backButton}
                        onClick={onClose}
                        disableRipple>
                        {'Back'}
                    </Button>
                    <Button variant="contained"
                        className={`${classes.saveButton} ${!isSaveDisabled ? classes.saveButtonActive : ''}`}
                        onClick={handleUpdateContext}
                        disabled={isSaveDisabled}
                        disableRipple>
                        {'Save'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ManageCustomTagsModal;